import React, {useContext, useEffect, useState} from 'react';
import {Button, Modal} from 'react-bootstrap';
import {Coordinates, ImageFile, PageImagePositionResponse} from '../../Models/Models';
import {EntityColors} from '../../Themes/EntityColors';
import ImageSelectorComponent from './ImageSelector';
import {Utils} from "../../Services/Utils";
import {ImageService} from "../../Services/ImageService";
import {useParams} from "react-router-dom";
import {SiteContext, ToastContext} from "../../Contexts";
import CreateButton from "../Buttons/CreateButton";
import ImageUploadComponent from "./ImageUpload";
import FormShell from "../Form/FormShell";
import DiscardButton from "../Buttons/DiscardButton";
import SaveButton from "../Buttons/SaveButton";
import PageImageCropper from "./PageImageCropper";


const PageImageChangeComponent: React.FC = () => {
    useEffect(() => {
        Utils.setDocumentTitle("Výber obrázku");
        void handleLoad();
    }, []);

    const {updatePageImage, getPageImage} = ImageService();
    const {action, icon} = EntityColors.image;

    const {positionId} = useParams<{ positionId: string }>();
    const {setToast}: any = useContext(ToastContext);
    const { currentSite}: any = useContext(SiteContext);

    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [modeSelectNew, setModeSelectNew] = useState(false);
    const [showImageUploadModal, setShowImageUploadModal] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [pageImage, setPageImage] = useState<PageImagePositionResponse | null>(null);
    const [coordinates, setCoordinates] = useState<Coordinates | null>(null);

    // Select-new flow: chosen image plus the window drawn in the confirm dialog
    const [selectedImage, setSelectedImage] = useState<ImageFile | null>(null);
    const [selectedCoordinates, setSelectedCoordinates] = useState<Coordinates | null>(null);

    const handleLoad = async () => {
        if (isNaN(Number(positionId))) {
            setToast({message: "Nesprávny positionId"});
            return;
        }

        const data = await getPageImage(Number(positionId));
        if (data) {
            setPageImage(data);
            setLoaded(true);
        }
    };

    const doSave = async (): Promise<boolean> => {
        if (!pageImage || !coordinates?.filled) return false;
        await updatePageImage({
            positionId: Number(positionId),
            imageId: pageImage.imageUsage.imageId,
            coordinates,
        }, pageImage.pageId);
        return true;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void doSave();
    };

    const doConfirmNew = async (): Promise<boolean> => {
        if (!pageImage || !selectedImage || !selectedCoordinates?.filled) return false;
        await updatePageImage({
            positionId: Number(positionId),
            imageId: selectedImage.id,
            coordinates: selectedCoordinates,
        }, pageImage.pageId);
        return true;
    };

    const tableHref = pageImage ? `/pageimage/table/${pageImage.pageId}` : '/pageimage/table';

    if (!modeSelectNew) {
        return (
            <FormShell
                title="Použitý obrázok"
                icon={icon}
                accentColor={action}
                backHref={tableHref}
                actions={
                    <button type="button" className="st-btn st-btn--tint st-btn--sm"
                            style={{'--btn-accent': action} as React.CSSProperties}
                            onClick={() => setModeSelectNew(true)}>
                        Vybrať nový
                    </button>
                }
            >
                {!loaded || !pageImage ? (
                    <p className="text-center py-4 text-secondary">Načítavam...</p>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <PageImageCropper
                            key={pageImage.imageUsage.imageId}
                            imageUrl={Utils.toOriginalImageUrl(pageImage.imageUsage.filePath)}
                            ratioWidth={pageImage.width}
                            ratioHeight={pageImage.height}
                            initial={Utils.parseCropName(pageImage.imageUsage.filePath)}
                            onChange={setCoordinates}
                        />
                        <div className="d-flex justify-content-center mt-4 gap-3">
                            <DiscardButton to={tableHref}/>
                            <SaveButton type="submit" onSave={doSave} websiteId={currentSite.id}/>
                        </div>
                    </form>
                )}
            </FormShell>
        );
    }

    return (
        <>
            <FormShell
                title="Vybrať nový obrázok"
                icon={icon}
                accentColor={action}
                onBack={() => setModeSelectNew(false)}
                actions={<CreateButton color={action} label={"Pridať"} onClick={() => setShowImageUploadModal(true)}/>}
            >
                <ImageSelectorComponent
                    accentColor={action}
                    selectedIds={pageImage ? [pageImage.imageUsage.imageId] : []}
                    handleImageClick={(image: ImageFile) => {
                        setSelectedCoordinates(null);
                        setSelectedImage(image);
                    }}
                    refreshTrigger={refreshTrigger}
                />
            </FormShell>

            <Modal show={showImageUploadModal} onHide={() => {
                setShowImageUploadModal(false);
                setRefreshTrigger(refreshTrigger + 1);
            }}
                   dialogClassName="modal-xl">
                <Modal.Body  className={"pb-4"}>
                    <ImageUploadComponent isPopUp={true} onClose={() => {
                        setShowImageUploadModal(false);
                        setRefreshTrigger(refreshTrigger + 1);
                    }}/>
                </Modal.Body>
            </Modal>

            {selectedImage && (
                <Modal show onHide={() => setSelectedImage(null)} size="lg">
                    <Modal.Header closeButton><Modal.Title>Nastaviť výrez obrázka</Modal.Title></Modal.Header>
                    <Modal.Body>
                        <PageImageCropper
                            key={selectedImage.id}
                            imageUrl={selectedImage.filePath}
                            ratioWidth={pageImage?.width ?? 400}
                            ratioHeight={pageImage?.height ?? 300}
                            initial={null}
                            onChange={setSelectedCoordinates}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline-secondary" onClick={() => setSelectedImage(null)}>Zavrieť</Button>
                        <SaveButton type="button" text="Uložiť výrez" onClick={() => void doConfirmNew()}
                                    onSave={doConfirmNew} websiteId={currentSite.id}/>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default PageImageChangeComponent;
